/**
 * Cognito Hosted UI / OAuth code-flow helpers.
 *
 * Reference: https://docs.aws.amazon.com/cognito/latest/developerguide/login-endpoint.html
 */

import { authConfig } from './config'

export interface TokenResponse {
  id_token: string
  access_token: string
  refresh_token?: string
  expires_in: number
  token_type: string
}

export function buildLoginUrl(opts: {
  codeChallenge: string
  state: string
}): string {
  const params = new URLSearchParams({
    response_type: 'code',
    client_id: authConfig.clientId,
    redirect_uri: authConfig.callbackUrl,
    scope: authConfig.scopes,
    code_challenge: opts.codeChallenge,
    code_challenge_method: 'S256',
    state: opts.state,
  })
  return `https://${authConfig.cognitoDomain}/oauth2/authorize?${params.toString()}`
}

export function buildLogoutUrl(): string {
  const params = new URLSearchParams({
    client_id: authConfig.clientId,
    logout_uri: authConfig.logoutRedirect,
  })
  return `https://${authConfig.cognitoDomain}/logout?${params.toString()}`
}

export async function exchangeCodeForTokens(opts: {
  code: string
  codeVerifier: string
}): Promise<TokenResponse> {
  const body = new URLSearchParams({
    grant_type: 'authorization_code',
    client_id: authConfig.clientId,
    code: opts.code,
    redirect_uri: authConfig.callbackUrl,
    code_verifier: opts.codeVerifier,
  })

  const res = await fetch(`https://${authConfig.cognitoDomain}/oauth2/token`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: body.toString(),
  })

  if (!res.ok) {
    const detail = await res.text().catch(() => '')
    throw new Error(`Token exchange failed: HTTP ${res.status} ${detail}`)
  }
  return res.json()
}

export interface DecodedIdTokenClaims {
  sub: string
  email?: string
  username?: string
  exp: number
  [key: string]: unknown
}

/**
 * Decode the JWT payload without verifying the signature. SAFE for use here
 * because the token is verified server-side by the API Gateway JWT authorizer
 * before any backend call lands. This is purely for displaying the user's
 * email/name in the SPA.
 */
export function decodeIdToken(idToken: string): DecodedIdTokenClaims {
  const parts = idToken.split('.')
  if (parts.length !== 3) throw new Error('Malformed JWT')
  const payload = parts[1].replace(/-/g, '+').replace(/_/g, '/')
  const padded = payload + '='.repeat((4 - (payload.length % 4)) % 4)
  return JSON.parse(atob(padded))
}
